import React, { useState, useMemo } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Paper, Tooltip, TableSortLabel, Box
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from "../../../Utils/FormatNumber";
import {
    enrichAssetsWithGroupSaleInfo,
    isAssetSold,
    formatAssetDescription,
} from "../../../Utils/assetSaleDisplayUtils";

const CELL_SX = {
    py: '6px', px: '12px', fontSize: 13, whiteSpace: 'normal', wordBreak: 'break-word',
    textAlign: 'right', borderBottom: '1px solid #e0e0e0', borderLeft: '1px solid #eee', verticalAlign: 'middle',
    color: '#222',
};
const HEADER_SX = {
    ...CELL_SX, fontWeight: 700, fontSize: 13, borderBottom: '2px solid #304FFF', borderLeft: '1px solid #ddd',
    backgroundColor: '#BFC8FF', color: '#304FFF', position: 'sticky', top: 0, zIndex: 10,
};
const GROUP_ROW_SX = {
    fontWeight: 800, fontSize: '0.95em', backgroundColor: '#e8ecf8',
    '& td': { borderBottom: '1px solid #b0b8d9', borderLeft: 'none', padding: '8px 12px', color: '#1a237e' },
};
const ASSET_ROW_SX = {
    cursor: 'pointer', transition: 'background 0.15s ease',
    '&:hover': { backgroundColor: '#eef1ff' },
};
const SOLD_ROW_SX = {
    ...ASSET_ROW_SX,
    backgroundColor: 'rgba(255, 82, 82, 0.10)', color: '#b71c1c',
    '&:hover': { backgroundColor: 'rgba(255, 82, 82, 0.18)' },
};
const SUM_ROW_SX = {
    '& td': { fontWeight: 700, fontSize: 13, backgroundColor: '#f0f2fa', whiteSpace: 'nowrap', padding: '8px 12px', color: '#333' },
};
const GRAND_TOTAL_SX = {
    '& td': { fontWeight: 800, fontSize: 14, backgroundColor: '#d6dcf5', whiteSpace: 'nowrap', padding: '10px 12px', color: '#1a237e' },
};

const COLUMNS = [
    { id: 'assetName', labelHe: 'רשימת הנכסים ותאורם', minWidth: 140, sortable: true, type: 'string' },
    { id: 'activationDate', labelHe: 'תאריך רכישה/הפעלה', minWidth: 100, sortable: true, type: 'date' },
    { id: 'originalPrice', labelHe: 'מחיר מקורי', minWidth: 90, sortable: true, type: 'number' },
    { id: 'depreciationPercentage', labelHe: 'אחוז פחת%', minWidth: 70, sortable: true, type: 'number' },
    { id: 'description', labelHe: 'הערות', minWidth: 120, sortable: true, type: 'string' },
    { id: 'sellDate', labelHe: 'תאריך מכירה', minWidth: 95, sortable: true, type: 'date' },
    { id: 'sellAmount', labelHe: 'סכום מכירה', minWidth: 90, sortable: true, type: 'number' },
];

function AssetTable({
    assets,
    assetGroups,
    assetRelations = [],
    expandedRows,
    handleExpandClick,
    onRowClick,
    searchQuery,
    showEmptyGroups = false,
    t: tProp,
}) {
    const { t: tHook, i18n } = useTranslation();
    const t = tProp || tHook;
    const dir = i18n.dir();
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc", type: "string" });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const displayAssets = useMemo(
        () => enrichAssetsWithGroupSaleInfo(assets, assetRelations),
        [assets, assetRelations]
    );

    const groups = useMemo(() => {
        const all = groupAssetsByGroup(displayAssets, assetGroups, t);
        return showEmptyGroups ? all : all.filter((g) => g.children?.length > 0);
    }, [displayAssets, assetGroups, showEmptyGroups, t]);

    const filteredAndSortedGroups = useMemo(() => {
        const q = (searchQuery || "").toLowerCase();
        let result = groups.map((group) => {
            const filtered = group.children.filter((a) =>
                (a.assetNumber?.toString() || '').includes(q) ||
                (a.assetName?.toLowerCase() || '').includes(q) ||
                (a.description?.toLowerCase() || '').includes(q) ||
                formatDate(a.purchaseDate).toLowerCase().includes(q) ||
                formatDate(a.activationDate).toLowerCase().includes(q) ||
                (a.originalPrice != null && a.originalPrice.toString().includes(q)) ||
                (a.depreciationPercentage != null && a.depreciationPercentage.toString().includes(q))
            );
            if (group.assetGroupName?.toLowerCase().includes(q) || filtered.length > 0) {
                return { ...group, children: filtered };
            }
            return null;
        }).filter(Boolean);

        if (sortConfig.key) {
            result.forEach((g) => {
                g.children.sort((a, b) => {
                    const av = a[sortConfig.key] ?? "";
                    const bv = b[sortConfig.key] ?? "";
                    const dir = sortConfig.direction === "asc" ? 1 : -1;
                    if (sortConfig.type === "number") return dir * ((Number(av) || 0) - (Number(bv) || 0));
                    if (sortConfig.type === "date") return dir * (new Date(av || 0) - new Date(bv || 0));
                    return dir * String(av).localeCompare(String(bv));
                });
            });
        }
        return result;
    }, [groups, searchQuery, sortConfig]);

    const currentGroups = useMemo(
        () => filteredAndSortedGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredAndSortedGroups, page, rowsPerPage]
    );

    const handleSort = (key, type) => {
        setSortConfig((prev) => ({
            key,
            type,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const isExpanded = (groupId) => expandedRows[groupId] !== false;
    const colCount = COLUMNS.length;

    const grandTotals = useMemo(() => {
        let totalOriginal = 0;
        filteredAndSortedGroups.forEach((g) => {
            g.children.forEach((a) => { totalOriginal += a.originalPrice || 0; });
        });
        return { totalOriginal };
    }, [filteredAndSortedGroups]);

    return (
        <>
            <TableContainer component={Paper} sx={{ height: "50vh", overflowX: "auto", overflowY: "auto", direction: 'ltr' }}>
                <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 13 }}>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((col) => (
                                <TableCell key={col.id} sx={HEADER_SX} align="right" style={{ minWidth: col.minWidth }}>
                                    {col.sortable ? (
                                        <TableSortLabel
                                            active={sortConfig.key === col.id}
                                            direction={sortConfig.direction === "asc" ? "asc" : "desc"}
                                            onClick={() => handleSort(col.id, col.type)}
                                        >
                                            {col.labelHe}
                                        </TableSortLabel>
                                    ) : (
                                        col.labelHe
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentGroups.map((group) => {
                            let sumOriginal = 0;
                            group.children.forEach((a) => { sumOriginal += a.originalPrice || 0; });

                            return (
                                <React.Fragment key={group.id}>
                                    <Tooltip title={t('DoubleClickToEdit')} placement="top">
                                        <TableRow
                                            sx={GROUP_ROW_SX}
                                            onDoubleClick={() => onRowClick(group.id, 'assetGroup')}
                                        >
                                            <TableCell sx={CELL_SX} colSpan={colCount}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {group.children.length > 0 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleExpandClick(group.id); }}
                                                            sx={{ p: 0 }}
                                                        >
                                                            {isExpanded(group.id) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                        </IconButton>
                                                    )}
                                                    <span>
                                                        {group.id === "NoGroup"
                                                            ? "פריטים ללא קבוצה"
                                                            : `קבוצה: ${group.id} - ${group.assetGroupName}`
                                                        }
                                                        {group.depreciationPercentage > 0 && ` (${group.depreciationPercentage}%)`}
                                                    </span>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </Tooltip>

                                    {isExpanded(group.id) && group.children.map((asset) => {
                                        const isSold = isAssetSold(asset);
                                        const displayDescription = formatAssetDescription(asset.description, asset.sellDate, isSold);
                                        return (
                                            <TableRow
                                                key={asset.id}
                                                sx={isSold ? SOLD_ROW_SX : ASSET_ROW_SX}
                                                onClick={() => onRowClick(asset.id, 'asset')}
                                            >
                                                <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{asset.assetNumber || '—'} - {asset.assetName || '—'}</TableCell>
                                                <TableCell sx={CELL_SX}>{formatDateHe(asset.activationDate)}</TableCell>
                                                <TableCell sx={{ ...CELL_SX, color: '#1565c0', fontWeight: 600 }}>{beautifyNumber(asset.originalPrice, 0)}</TableCell>
                                                <TableCell sx={{ ...CELL_SX, color: '#6a1b9a' }}>{asset.depreciationPercentage != null ? `${beautifyNumber(asset.depreciationPercentage, 0)}%` : '—'}</TableCell>
                                                <TableCell sx={{ ...CELL_SX, color: isSold ? '#c62828' : '#555', fontSize: 12 }}>{displayDescription}</TableCell>
                                                <TableCell sx={{ ...CELL_SX, color: isSold ? '#c62828' : '#999' }}>{asset.sellDate ? formatDateHe(asset.sellDate) : '—'}</TableCell>
                                                <TableCell sx={{ ...CELL_SX, color: isSold ? '#c62828' : '#999', fontWeight: isSold ? 600 : 400 }}>
                                                    {asset.sellAmount != null && asset.sellAmount !== '' ? beautifyNumber(asset.sellAmount, 0) : '—'}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {isExpanded(group.id) && group.children.length > 0 && (
                                        <TableRow sx={SUM_ROW_SX}>
                                            <TableCell sx={CELL_SX} colSpan={2}>סה"כ לקבוצה {group.assetGroupName || ''}</TableCell>
                                            <TableCell sx={CELL_SX}>{beautifyNumber(sumOriginal, 0)}</TableCell>
                                            <TableCell sx={CELL_SX}>-</TableCell>
                                            <TableCell sx={CELL_SX}></TableCell>
                                            <TableCell sx={CELL_SX}></TableCell>
                                            <TableCell sx={CELL_SX}></TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}

                    </TableBody>
                </Table>
            </TableContainer>
            {currentGroups.length > 0 && (
                <Table size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 14, direction: 'ltr' }}>
                    <TableBody>
                        <TableRow sx={GRAND_TOTAL_SX}>
                            <TableCell sx={{ ...CELL_SX, minWidth: 140 }} colSpan={2}>סה״כ כללי</TableCell>
                            <TableCell sx={{ ...CELL_SX, minWidth: 90 }}>{beautifyNumber(grandTotals.totalOriginal, 0)}</TableCell>
                            <TableCell sx={{ ...CELL_SX, minWidth: 70 }}>-</TableCell>
                            <TableCell sx={{ ...CELL_SX, minWidth: 120 }}></TableCell>
                            <TableCell sx={{ ...CELL_SX, minWidth: 95 }}></TableCell>
                            <TableCell sx={{ ...CELL_SX, minWidth: 90 }}></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            )}
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredAndSortedGroups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t("RowsPerPage")}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("of")} ${count}`}
                sx={{
                    direction: dir,
                    display: 'flex',
                    justifyContent: dir === 'rtl' ? 'flex-end' : 'flex-start',
                    borderTop: '1px solid #e5e7f6',
                    overflow: 'visible',
                    fontFamily: 'var(--xtram-font)',
                    color: 'var(--xtram-text)',
                    '& .MuiTablePagination-spacer': {
                        display: 'none',
                    },
                    '& .MuiTablePagination-toolbar': {
                        minHeight: 40,
                        paddingInline: 8,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        margin: 0,
                        fontSize: 13,
                        fontFamily: 'var(--xtram-font)',
                        color: 'var(--xtram-text)',
                    },
                    '& .MuiTablePagination-displayedRows': {
                        direction: 'ltr',
                        unicodeBidi: 'isolate',
                    },
                    '& .MuiTablePagination-select': {
                        fontFamily: 'var(--xtram-font)',
                        fontSize: 13,
                    },
                    '& .MuiTablePagination-actions': {
                        marginInlineStart: 8,
                        flexDirection: dir === 'rtl' ? 'row-reverse' : 'row',
                    },
                    '& .MuiIconButton-root': {
                        transform: dir === 'rtl' ? 'scaleX(-1)' : 'none',
                    },
                }}
            />
        </>
    );
}

function groupAssetsByGroup(assets, assetGroups, t) {
    const map = {};
    (assetGroups || []).forEach((g) => {
        map[g.assetGroupId] = {
            id: g.assetGroupId,
            assetGroupName: g.name,
            depreciationPercentage: g.depreciationPercentage ?? 0,
            children: [],
        };
    });
    map["NoGroup"] = { id: "NoGroup", assetGroupName: t?.('NoGroup') || "ללא קבוצה", depreciationPercentage: 0, children: [] };

    (assets || []).forEach((a) => {
        const gid = a.assetGroupId && map[a.assetGroupId] ? a.assetGroupId : "NoGroup";
        map[gid].children.push({
            id: a.id,
            assetNumber: a.assetNumber,
            assetName: a.name,
            purchaseDate: a.purchaseDate,
            activationDate: a.activationDate || a.purchaseDate,
            originalPrice: a.originalPrice,
            depreciationPercentage: a.depreciationPercentage,
            description: a.description,
            sellDate: a.sellDate,
            sellAmount: a.sellAmount,
            saleIncomeId: a.saleIncomeId,
            saleJournalEntryId: a.saleJournalEntryId,
            soldViaAssetGroup: a.soldViaAssetGroup,
        });
    });
    return Object.values(map);
}

const formatDate = (d) => (!d ? "-" : new Date(d).toLocaleDateString());
const formatDateHe = (d) =>
    !d ? "—" : new Date(d).toLocaleDateString("he-IL");

export default AssetTable;
